import { useEffect, useRef, useState } from "react";
import {
  CheckCircle2, Download, Loader2, Film, Image as ImageIcon, FileText, Captions,
  FileType2, Sparkles, Clapperboard, ShieldCheck, Clock, DollarSign, Gauge,
  Music2, Mic2, Ratio, MonitorPlay, Droplet, Layers, Copy, Check as CheckIcon,
  ShieldAlert, AlertTriangle, XCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, Pill, Spinner, cx } from "@/components/shared";

async function downloadFile(url, filename) {
  try {
    const res = await fetch(mediaUrl(url));
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename || "vyom-download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(mediaUrl(url), "_blank");
  }
}

const SUMMARY_ICONS = {
  duration: Clock, resolution: MonitorPlay, aspect_ratio: Ratio, render_mode: Gauge,
  voice: Mic2, music: Music2, subtitle_status: Captions, quality_score: ShieldCheck,
  generation_time: Clock, watermark_status: Droplet, scenes: Layers,
};

export default function ProductionCenter({ pid, proj }) {
  const [summary, setSummary] = useState(null);
  const [dl, setDl] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rendering, setRendering] = useState({});
  const [cine, setCine] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [kit, setKit] = useState(null);
  const [kitLoading, setKitLoading] = useState(false);
  const [styleStale, setStyleStale] = useState(false);
  const [brand, setBrand] = useState(null);
  const timer = useRef(null);

  const loadAll = async () => {
    const [s, d, b] = await Promise.all([
      api.get(`/projects/${pid}/production-summary`),
      api.get(`/projects/${pid}/downloads`),
      api.get(`/projects/${pid}/brand-report`).catch(() => ({ data: null })),
    ]);
    setSummary(s.data);
    setDl(d.data);
    setBrand(b.data);
    return d.data;
  };

  useEffect(() => {
    loadAll().catch((e) => console.error("[ProductionCenter] load failed", e));
    api.get(`/projects/${pid}/cinematic`).then(({ data }) => setCine(data))
      .catch((e) => console.error("[ProductionCenter] cinematic load failed", e));
    return () => clearTimeout(timer.current);
  }, [pid]); // eslint-disable-line

  const changePreset = async (preset) => {
    setBriefLoading(true);
    try {
      const { data } = await api.post(`/projects/${pid}/cinematic/brief`, { preset });
      setCine((c) => ({ ...(c || {}), preset: data.preset, brief: data.brief, shot_plan: data.shot_plan }));
      if (dl?.videos?.some((v) => v.status === "ready")) setStyleStale(true);
      toast.success(`Directed as "${preset}" — re-render an export to apply`);
    } catch (e) { toast.error("Could not build brief"); }
    finally { setBriefLoading(false); }
  };

  const loadKit = async () => {
    setKitLoading(true);
    try { const { data } = await api.get(`/projects/${pid}/export-kit`); setKit(data); }
    catch (e) { toast.error("Could not build export kit"); }
    finally { setKitLoading(false); }
  };

  const poll = async (n = 0) => {
    const d = await loadAll();
    if (n < 60 && d.videos.some((v) => v.status === "rendering")) {
      timer.current = setTimeout(() => poll(n + 1), 4000);
    }
  };

  const approve = async () => {
    setApproving(true);
    try { await api.post(`/projects/${pid}/approve`); toast.success("Video approved — ready to export & download"); loadAll(); }
    catch { toast.error("Could not approve"); } finally { setApproving(false); }
  };

  const render = async (target) => {
    setRendering((r) => ({ ...r, [target]: true }));
    try {
      await api.post(`/projects/${pid}/render/${target}`);
      setStyleStale(false);
      toast.success("Rendering export — it will appear below shortly");
      poll();
    } catch (e) { toast.error(e.response?.data?.detail || "Render failed"); }
    finally { setRendering((r) => ({ ...r, [target]: false })); }
  };

  if (!summary || !dl) return <Spinner label="Loading production center…" />;

  const isReady = proj?.status === "ready";

  const summaryRows = [
    ["Video Duration", summary.duration, "duration"],
    ["Resolution", summary.resolution, "resolution"],
    ["Aspect Ratio", summary.aspect_ratio, "aspect_ratio"],
    ["Render Mode", summary.render_mode, "render_mode"],
    ["Voice Used", summary.voice, "voice"],
    ["Music Used", `${summary.music}${summary.music_volume === "low" ? " · low" : ""}`, "music"],
    ["Subtitle Status", summary.subtitle_status, "subtitle_status"],
    ["Quality Score", `${summary.quality_score}/100`, "quality_score"],
    ["Generation Time", summary.generation_time, "generation_time"],
    ["Total AI Cost", `$${summary.total_cost_usd} · ₹${summary.total_cost_inr}`, "quality_score"],
    ["Watermark", summary.watermark_status, "watermark_status"],
    ["Scenes", summary.scenes, "scenes"],
  ];

  return (
    <div className="space-y-6" data-testid="production-center">
      {/* Approve banner */}
      {!summary.approved ? (
        <GlassCard className="p-5 flex flex-col sm:flex-row sm:items-center gap-4 border-[#FFB300]/30">
          <div className="flex-1">
            <div className="font-display font-semibold text-white">Approve your final cut</div>
            <p className="text-xs text-slate-400 mt-1">Happy with the preview and edits? Approve to unlock exports & downloads. Nothing is published — you download and upload manually.</p>
          </div>
          <button onClick={approve} disabled={approving || !isReady} data-testid="approve-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FFB300] text-black font-medium text-sm disabled:opacity-50 hover:brightness-110">
            {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {isReady ? "Approve video" : "Finish generating first"}
          </button>
        </GlassCard>
      ) : (
        <GlassCard className="p-4 flex items-center gap-3 border-emerald-500/30" data-testid="approved-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <span className="text-sm text-white">Approved.</span>
          <span className="text-xs text-slate-400">Export any format below and download everything from one place.</span>
        </GlassCard>
      )}

      {/* FINAL MASTER — the single authoritative production */}
      <FinalMaster summary={summary} dl={dl} onRender={render} rendering={rendering} ffmpeg={dl.ffmpeg} />

      {/* Production summary */}
      <div className="grid gap-6">
        <GlassCard className="p-5">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-3">Production Summary</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
            {summaryRows.map(([label, value, ic]) => {
              const Icon = SUMMARY_ICONS[ic] || Sparkles;
              return (
                <div key={label} className="flex items-start gap-2">
                  <Icon className="w-4 h-4 text-[#5B4DFF] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{label}</div>
                    <div className="text-sm text-white truncate">{value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </GlassCard>
      </div>

      {/* Cinematic direction */}
      {cine && (
        <GlassCard className="p-5" data-testid="cinematic-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300]">Cinematic Direction</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Preset</span>
              <select value={cine.preset || ""} onChange={(e) => changePreset(e.target.value)} disabled={briefLoading}
                data-testid="cinematic-preset" className="bg-white/5 border border-white/10 rounded-lg text-xs text-slate-200 px-2 py-1.5 outline-none">
                {(cine.presets || []).map((p) => <option key={p} value={p} className="bg-[#0A1435]">{p}</option>)}
              </select>
              {briefLoading && <Loader2 className="w-4 h-4 animate-spin text-[#5B4DFF]" />}
            </div>
          </div>
          {cine.brief ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
              {["genre", "story_arc", "emotional_curve", "camera_language", "editing_style", "pacing",
                "colour_palette", "lighting_style", "hook_strategy", "ending_strategy", "retention_strategy", "music_style"]
                .filter((k) => cine.brief[k]).map((k) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{k.replace(/_/g, " ")}</div>
                    <div className="text-xs text-slate-200">{cine.brief[k]}</div>
                  </div>
                ))}
            </div>
          ) : (
            <button onClick={() => changePreset(cine.preset || "Hollywood Cinematic")} data-testid="build-brief"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs bg-[#5B4DFF] text-white hover:bg-[#6d5fff]">
              <Sparkles className="w-4 h-4" /> Direct this film
            </button>
          )}
          {cine.shot_plan?.shots?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-slate-400">
              Shot plan: {cine.shot_plan.shots.length} scenes directed ·
              {" "}{cine.shot_plan.shots.filter((s) => s.classification === "Hero").length} hero,
              {" "}{cine.shot_plan.shots.filter((s) => s.classification === "B-Roll").length} b-roll
            </div>
          )}
        </GlassCard>
      )}

      {/* Export options */}
      <GlassCard className="p-5">
        <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-1">Export Options</div>
        <p className="text-xs text-slate-500 mb-4">Render each format independently (AI-recomposed from your scenes + narration + music). No publishing — download only.</p>
        {styleStale && (
          <div className="text-xs text-[#FFB300] mb-3 flex items-center gap-1.5" data-testid="style-stale">
            <Sparkles className="w-3.5 h-3.5" /> Cinematic style changed — re-render to apply the new look to your downloads.
          </div>
        )}
        {!dl.ffmpeg && <div className="text-xs text-[#FFB300] mb-3">Renderer unavailable in this environment — text downloads still work.</div>}
        <div className="grid sm:grid-cols-2 gap-3">
          {dl.videos.map((v) => (
            <div key={v.id} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10" data-testid={`export-row-${v.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-white flex items-center gap-2"><Clapperboard className="w-4 h-4 text-[#5B4DFF]" /> {v.label}</div>
                <div className="text-[11px] text-slate-500">{v.aspect}{v.size_mb ? ` · ${v.size_mb} MB` : ""}</div>
                {v.status === "failed" && v.note && <div className="text-[10px] text-red-400 mt-0.5 truncate max-w-[220px]">{v.note}</div>}
                {v.status === "blocked" && <div className="text-[10px] text-[#FFB300] mt-0.5 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Branding check failed — download blocked</div>}
                {v.brand_qa?.result === "pass" && <div className="text-[10px] text-emerald-400 mt-0.5 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Brand verified · {v.brand_qa.score}/100</div>}
              </div>
              {v.downloadable && v.url ? (
                <button onClick={() => downloadFile(v.url, v.filename)} data-testid={`download-${v.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-emerald-500 text-white hover:bg-emerald-400">
                  <Download className="w-4 h-4" /> Download
                </button>
              ) : v.status === "rendering" ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-[#FFB300]"><Loader2 className="w-4 h-4 animate-spin" /> Rendering + verifying brand</span>
              ) : v.status === "blocked" ? (
                <button onClick={() => render(v.id)} disabled={!dl.ffmpeg || rendering[v.id]} data-testid={`render-${v.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-[#FFB300] text-black hover:brightness-110 disabled:opacity-50">
                  {rendering[v.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />} Re-run brand fix
                </button>
              ) : (
                <button onClick={() => render(v.id)} disabled={!dl.ffmpeg || rendering[v.id]} data-testid={`render-${v.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-[#5B4DFF] text-white hover:bg-[#6d5fff] disabled:opacity-50">
                  {rendering[v.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />} Render
                </button>
              )}
            </div>
          ))}
        </div>
      </GlassCard>

      {/* Brand Consistency Validator — final quality gate */}
      <BrandQACard brand={brand} />

      {/* Creator Export Kit */}
      <GlassCard className="p-5" data-testid="export-kit">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300]">Creator Export Kit</div>
          <button onClick={loadKit} disabled={kitLoading} data-testid="build-export-kit"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs bg-[#5B4DFF] text-white hover:bg-[#6d5fff] disabled:opacity-50">
            {kitLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {kit ? "Regenerate" : "Generate SEO kit"}
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Ready-to-paste titles, descriptions, chapters, tags & hashtags for YouTube and Instagram.</p>
        {kit ? (
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="text-xs font-semibold text-white flex items-center gap-2"><Clapperboard className="w-4 h-4 text-red-400" /> YouTube</div>
              <KitField label="SEO Title" value={kit.youtube?.seo_title} />
              <KitField label="Description" value={kit.youtube?.description} multiline />
              <KitField label="Chapters" value={(kit.youtube?.chapters || []).join("\n")} multiline />
              <KitField label="Tags" value={(kit.youtube?.tags || []).join(", ")} />
              <KitField label="Hashtags" value={(kit.youtube?.hashtags || []).join(" ")} />
              <KitField label="Pinned Comment" value={kit.youtube?.pinned_comment} multiline />
              <KitField label="Community Post" value={kit.youtube?.community_post} multiline />
              <KitField label="End Screen CTA" value={kit.youtube?.end_screen_cta} />
            </div>
            <div className="space-y-2">
              <div className="text-xs font-semibold text-white flex items-center gap-2"><ImageIcon className="w-4 h-4 text-pink-400" /> Instagram</div>
              <KitField label="Hook" value={kit.instagram?.hook} />
              <KitField label="Caption" value={kit.instagram?.caption} multiline />
              <KitField label="Hashtags" value={(kit.instagram?.hashtags || []).join(" ")} />
              <KitField label="First Comment" value={kit.instagram?.first_comment} multiline />
              <KitField label="CTA" value={kit.instagram?.cta} />
              <KitField label="Cover Idea" value={kit.instagram?.cover_idea} />
            </div>
          </div>
        ) : (
          <div className="text-xs text-slate-500">Generate the kit to get platform-optimized publishing copy.</div>
        )}
      </GlassCard>

      {/* Download center */}
      <GlassCard className="p-5">
        <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-1">Download Center</div>
        <p className="text-xs text-slate-500 mb-4">Everything for this project in one place.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {dl.videos.filter((v) => v.downloadable && v.url).map((v) => (
            <DownloadRow key={v.id} icon={Clapperboard} label={`Video · ${v.label}`} meta={`${v.aspect}${v.size_mb ? ` · ${v.size_mb} MB` : ""} · brand ✓`} onClick={() => downloadFile(v.url, v.filename)} testid={`dl-video-${v.id}`} />
          ))}
          {dl.text.thumbnail?.ready && <DownloadRow icon={ImageIcon} label="Thumbnail" meta="PNG" onClick={() => downloadFile(dl.text.thumbnail.url, dl.text.thumbnail.filename)} testid="dl-thumbnail" />}
          {dl.text.subtitles?.ready && <DownloadRow icon={Captions} label="Subtitles" meta=".srt" onClick={() => downloadFile(dl.text.subtitles.url, dl.text.subtitles.filename)} testid="dl-subtitles" />}
          {dl.text.transcript?.ready && <DownloadRow icon={FileText} label="Transcript" meta=".txt" onClick={() => downloadFile(dl.text.transcript.url, dl.text.transcript.filename)} testid="dl-transcript" />}
          {dl.text.report?.ready && <DownloadRow icon={FileType2} label="Generation Report" meta=".pdf" onClick={() => downloadFile(dl.text.report.url, dl.text.report.filename)} testid="dl-report" />}
        </div>
        {!dl.videos.some((v) => v.downloadable) && (
          <div className="text-xs text-slate-500 mt-3">Render an export above — brand-verified videos appear here once the Brand Consistency check passes.</div>
        )}
      </GlassCard>
    </div>
  );
}

const PSTATUS = {
  DOWNLOADABLE: { label: "PRODUCTION READY", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", Icon: CheckCircle2 },
  PRODUCTION_READY: { label: "PRODUCTION READY", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", Icon: CheckCircle2 },
  REVIEW_REQUIRED: { label: "REVIEW REQUIRED", cls: "text-[#FFB300] border-[#FFB300]/40 bg-[#FFB300]/10", Icon: AlertTriangle },
  BUILDING: { label: "BUILDING", cls: "text-[#5B8CFF] border-[#5B8CFF]/40 bg-[#5B8CFF]/10", Icon: Loader2 },
  BLOCKED: { label: "PRODUCTION BLOCKED", cls: "text-red-400 border-red-500/40 bg-red-500/10", Icon: ShieldAlert },
};

function FinalMaster({ summary, dl, onRender, rendering, ffmpeg }) {
  const m = summary.master || {};
  const mq = m.master_qa || {};
  const st = PSTATUS[summary.production_status] || PSTATUS.REVIEW_REQUIRED;
  const ig = (dl.videos || []).find((v) => v.id === "instagram") || {};
  const playable = m.downloadable && m.url;
  const busy = rendering.youtube || m.status === "rendering";
  const aspect = summary.aspect_ratio || "16:9";

  const meta = [
    ["Duration", mq.duration ? `${Math.round(mq.duration)}s` : summary.duration],
    ["Resolution", summary.resolution],
    ["Aspect", aspect],
    ["Audio", "Narration + Music"],
    ["Subtitles", summary.subtitle_status?.startsWith("Ready") ? "Ready" : "—"],
    ["Watermark", m.brand_result === "pass" ? "Verified · once" : "Pending"],
    ["Brand QA", m.brand_score != null ? `${m.brand_score}/100` : "—"],
    ["Scenes", summary.scenes],
  ];

  return (
    <GlassCard className="p-5" data-testid="final-master-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] flex items-center gap-2">
          <Clapperboard className="w-4 h-4" /> Final Video
        </div>
        <span className={cx("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium", st.cls)} data-testid="production-status">
          <st.Icon className={cx("w-3.5 h-3.5", summary.production_status === "BUILDING" && "animate-spin")} /> {st.label}
        </span>
      </div>

      <div className={cx("rounded-xl overflow-hidden bg-black mx-auto", aspect === "16:9" ? "aspect-video w-full" : "aspect-[9/16] max-w-[300px]")}>
        {playable ? (
          <video src={mediaUrl(m.url)} controls className="w-full h-full object-contain" data-testid="final-video" />
        ) : (
          <div className="w-full h-full grid place-items-center relative">
            {summary.thumbnail_url && <img src={mediaUrl(summary.thumbnail_url)} alt="poster" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
            <div className="relative text-center px-4">
              {summary.production_status === "BLOCKED" ? (
                <>
                  <ShieldAlert className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <div className="text-sm text-red-300" data-testid="master-blocked-reason">{m.note || "Production blocked"}</div>
                </>
              ) : (
                <>
                  <Film className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <div className="text-sm text-slate-300">Build your final video to preview it here</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {playable && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 mt-4">
          {meta.map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{k}</div>
              <div className="text-sm text-white truncate">{String(v)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mt-4">
        {playable ? (
          <>
            <button onClick={() => downloadFile(m.url, m.filename)} data-testid="download-master-youtube"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-white font-medium text-sm hover:bg-emerald-400">
              <Download className="w-4 h-4" /> Download YouTube Video
            </button>
            {ig.downloadable && ig.url ? (
              <button onClick={() => downloadFile(ig.url, ig.filename)} data-testid="download-master-instagram"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 border border-white/15 text-white font-medium text-sm hover:bg-white/20">
                <Download className="w-4 h-4" /> Download Instagram Reel
              </button>
            ) : (
              <button onClick={() => onRender("instagram")} disabled={!ffmpeg || rendering.instagram} data-testid="build-master-instagram"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 border border-white/15 text-white font-medium text-sm hover:bg-white/20 disabled:opacity-50">
                {rendering.instagram ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />} Build Instagram Reel
              </button>
            )}
            <button onClick={() => onRender("youtube")} disabled={!ffmpeg || busy} data-testid="rebuild-master"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-slate-300 hover:text-white text-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Rebuild
            </button>
          </>
        ) : (
          <button onClick={() => onRender("youtube")} disabled={!ffmpeg || busy} data-testid="build-master"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#5B4DFF] text-white font-medium text-sm hover:bg-[#4a3ff0] disabled:opacity-50">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Building final video…</> : <><Film className="w-4 h-4" /> {summary.production_status === "BLOCKED" ? "Rebuild final video" : "Build final video"}</>}
          </button>
        )}
        {!ffmpeg && <span className="text-xs text-[#FFB300]">Renderer unavailable in this environment.</span>}
      </div>
    </GlassCard>
  );
}


function KitField({ label, value, multiline }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(String(value)).catch(() => {});
      else {
        const ta = document.createElement("textarea");
        ta.value = String(value); document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); ta.remove();
      }
    } catch { /* clipboard blocked — icon still confirms intent */ }
  };
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-2.5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{label}</span>
        <button onClick={copy} className="text-slate-400 hover:text-white" data-testid={`copy-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          {copied ? <CheckIcon className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className={`text-xs text-slate-200 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{value}</div>
    </div>
  );
}

function DownloadRow({ icon: Icon, label, meta, onClick, testid }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="w-4 h-4 text-[#5B4DFF] shrink-0" />
        <div className="min-w-0"><div className="text-sm text-white truncate">{label}</div><div className="text-[11px] text-slate-500">{meta}</div></div>
      </div>
      <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-white/10 border border-white/10 text-white hover:bg-white/20">
        <Download className="w-4 h-4" /> Download
      </button>
    </div>
  );
}

const CHECK_ICON = {
  pass: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />,
  warn: <AlertTriangle className="w-3.5 h-3.5 text-[#FFB300] shrink-0" />,
  fail: <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />,
};

function BrandQACard({ brand }) {
  const [open, setOpen] = useState({});
  const validated = brand?.validated;
  const overall = brand?.overall_result || "pending";
  const badge = overall === "pass"
    ? { cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", label: "PASS", Icon: ShieldCheck }
    : overall === "fail"
    ? { cls: "text-red-400 border-red-500/40 bg-red-500/10", label: "FAIL", Icon: ShieldAlert }
    : { cls: "text-slate-400 border-white/15 bg-white/5", label: "PENDING", Icon: ShieldCheck };

  return (
    <GlassCard className="p-5" data-testid="brand-qa-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Brand Consistency Validator
        </div>
        <div className="flex items-center gap-2">
          {validated && <span className="text-xs text-slate-400">Overall {brand.overall_score}/100</span>}
          <span className={cx("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium", badge.cls)} data-testid="brand-qa-result">
            <badge.Icon className="w-3.5 h-3.5" /> {badge.label}
          </span>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">The final quality gate — every export is checked for correct watermark, adaptive colour, safe placement, preview parity and brand metadata before its download unlocks. Branding is repaired automatically; a failed export stays blocked.</p>

      {!validated ? (
        <div className="text-xs text-slate-500">Render an export above to run the Brand Consistency check and generate its Branding QA Report.</div>
      ) : (
        <div className="space-y-3">
          {brand.exports.map((ex) => {
            const isOpen = open[ex.id];
            const exPass = ex.result === "pass";
            return (
              <div key={ex.id} className="rounded-xl bg-white/5 border border-white/10" data-testid={`brand-qa-${ex.id}`}>
                <button onClick={() => setOpen((o) => ({ ...o, [ex.id]: !o[ex.id] }))}
                  className="w-full flex items-center justify-between gap-3 p-3 text-left" data-testid={`brand-qa-toggle-${ex.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {exPass ? <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" /> : <ShieldAlert className="w-4 h-4 text-red-400 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{ex.label} <span className="text-slate-500">· {ex.aspect}</span></div>
                      <div className="text-[11px] text-slate-500">{ex.summary}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={cx("text-xs font-medium", exPass ? "text-emerald-400" : "text-red-400")}>{ex.score}/100</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3">
                    <div className="grid sm:grid-cols-3 gap-x-4 gap-y-1.5 mb-3 pt-2 border-t border-white/5">
                      {[["Variant", (ex.variants_used || []).join(", ") || ex.variant],
                        ["Position", ex.position], ["Opacity", ex.opacity], ["Size", ex.size],
                        ["Metadata", ex.metadata_status], ["Preview Match", ex.preview_match ? "Yes" : "No"],
                        ["Export Match", ex.export_match ? "Yes" : "No"], ["Scenes", ex.scenes],
                        ["Result", exPass ? "PASS" : "FAIL"]].map(([k, v]) => (
                        <div key={k}>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{k}</div>
                          <div className="text-xs text-slate-200">{String(v)}</div>
                        </div>
                      ))}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
                      {(ex.checks || []).map((c) => (
                        <div key={c.id} className="flex items-start gap-1.5" title={c.detail}>
                          {CHECK_ICON[c.status]}
                          <span className="text-[11px] text-slate-300">{c.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
}
